import { CandidateValidationService } from '../services/candidate-validation';
import { downloadCandidateCV } from '../services/candidates';

export interface CVDownloadResult {
    success: boolean;
    blob?: Blob;
    error?: string;
    missingFields?: string[];
    isValidationError?: boolean;
}

/**
 * Utilitário para download do currículo com verificação de validação
 */
export class CVDownloadHelper {
    /**
     * Verifica se o perfil está válido antes de tentar baixar o currículo
     * @param candidateId ID do candidato
     * @returns Promise com resultado da verificação
     */
    static async checkProfileBeforeDownload(candidateId: string): Promise<{
        isValid: boolean;
        missingFields: string[];
        message: string;
    }> {
        try {
            const validation = await CandidateValidationService.validateProfile(candidateId);
            return {
                isValid: validation.is_valid,
                missingFields: validation.missing_fields || [],
                message: this.extractMessage(validation.message)
            };
        } catch (error: any) {
            return {
                isValid: false,
                missingFields: [],
                message: error.message || 'Erro ao verificar perfil'
            };
        }
    }

    /**
     * Tenta baixar o currículo com verificação prévia de validação
     * @param candidateId ID do candidato
     * @param skipValidation Se deve pular a verificação de validação (padrão: false)
     * @returns Promise com resultado do download
     */
    static async downloadCVWithValidation(
        candidateId: string, 
        skipValidation: boolean = false
    ): Promise<CVDownloadResult> {
        try {
            // Verificar validação se não foi solicitado para pular
            if (!skipValidation) {
                const validation = await this.checkProfileBeforeDownload(candidateId);
                
                if (!validation.isValid) {
                    return {
                        success: false,
                        error: validation.message,
                        missingFields: validation.missingFields,
                        isValidationError: true
                    };
                }
            }

            // Tentar baixar o currículo
            const blob = await downloadCandidateCV(candidateId);
            
            return {
                success: true,
                blob
            };
        } catch (error: any) {
            // Verificar se é erro de validação baseado na mensagem
            const errorMessage = error.message || '';
            const isValidationError = errorMessage.includes('Perfil incompleto') || 
                                    errorMessage.includes('Campos obrigatórios faltantes');
            
            return {
                success: false,
                error: errorMessage,
                isValidationError
            };
        }
    }

    /**
     * Extrai a mensagem de forma segura, lidando com arrays vazios
     * @param message Mensagem que pode ser string ou array de strings
     * @returns String da mensagem ou mensagem padrão
     */
    private static extractMessage(message: string | string[]): string {
        if (Array.isArray(message)) {
            return message.length > 0 ? message[0] : 'Erro de validação';
        }
        return message || 'Erro de validação';
    }

    /**
     * Cria um link de download para o blob do currículo
     * @param blob Blob do currículo
     * @param filename Nome do arquivo (padrão: 'curriculo.pdf')
     */
    static createDownloadLink(blob: Blob, filename: string = 'curriculo.pdf'): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    /**
     * Função completa para download do currículo com todas as verificações
     * @param candidateId ID do candidato
     * @param filename Nome do arquivo para download
     * @param skipValidation Se deve pular a verificação de validação
     * @returns Promise com resultado do download
     */
    static async downloadCV(
        candidateId: string,
        filename: string = 'curriculo.pdf',
        skipValidation: boolean = false
    ): Promise<CVDownloadResult> {
        const result = await this.downloadCVWithValidation(candidateId, skipValidation);
        
        if (result.success && result.blob) {
            this.createDownloadLink(result.blob, filename);
        }
        
        return result;
    }
}
